// Kontener szczegółów oferty pod listą pozycji: taby (Wysyłka, Komentarze, Uwagi klienta, Usługi dodatkowe)
// + boczne podsumowanie wartości netto/brutto.

import React, { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Tab, Tabs, TextField } from '@mui/material';

import {
  useGetSalesCrmClientDeliveryMethods,
  useGetSalesCrmClientReceivers,
  useGetSalesCrmOfferAllServices,
  useGetSalesCrmOfferShipping,
  useGetSalesCrmOfferTotals,
  usePutSalesCrmOfferShipping
} from 'api';
import { ISalesCrmReceiverOption } from 'api/endpoints/useGetSalesCrmClientReceivers';

import OfferCommentsCard from './OfferCommentsCard';

import styles from 'theme/components/containers/TraderClientOfferShippingDetailsContainer/TraderClientOfferShippingDetailsContainer.module.scss';

interface IProps {
  clientId: number;
  offerId: string;
  /** Wymuszona wersja oferty (null => aktualna). */
  versionId?: number | null;
}

const TraderClientOfferShippingDetailsContainer: React.FC<IProps> = ({
  clientId,
  offerId,
  versionId = null
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  // Słowniki do selectów
  const { data: deliveryMethodsData } = useGetSalesCrmClientDeliveryMethods(clientId);
  const { data: receiversData } = useGetSalesCrmClientReceivers(clientId);

  // Dane wysyłki dla wybranej wersji oferty
  const { data: shipping } = useGetSalesCrmOfferShipping(clientId, offerId, versionId);

  // Podsumowanie wartości oferty z encji OfferVersion (single source of truth).
  // Inwalidowane przez: PUT shipping (tutaj) oraz CRUD pozycji (TraderClientOfferProductsContainer).
  const { data: totals } = useGetSalesCrmOfferTotals(clientId, offerId, versionId);

  // Płaska lista wszystkich usług aktualnej wersji oferty — pokazywana pod sekcją Wysyłka
  // w sidebarze totalów, żeby handlowiec widział co składa się na "Razem netto".
  const { data: allServicesData } = useGetSalesCrmOfferAllServices(clientId, offerId);
  const allServices = allServicesData?.items ?? [];

  const productNet = totals?.productNet ?? 0;
  const shippingNet = totals?.shippingNet ?? 0;
  const totalNet = totals?.totalNet ?? 0;
  const vat = totals?.vat ?? 0;
  const totalGross = totals?.totalGross ?? 0;
  const marginNet = totals?.marginNet ?? null;
  const marginPct = totals?.marginPct ?? null;

  const invalidateShipping = () => {
    queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId, 'shipping']);
    queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId, 'totals']);
  };

  const { mutate: putShipping, isLoading: isSaving } = usePutSalesCrmOfferShipping(
    clientId,
    offerId,
    { onSuccess: invalidateShipping }
  );

  const handleFieldChange = (
    field: keyof NonNullable<typeof shipping>,
    rawValue: string,
    type: 'int' | 'float' | 'string' | 'bool' = 'string'
  ) => {
    let value: number | string | boolean | null;
    if (rawValue === '') {
      value = null;
    } else if (type === 'int') {
      const parsed = parseInt(rawValue, 10);
      value = Number.isNaN(parsed) ? null : parsed;
    } else if (type === 'float') {
      const parsed = parseFloat(rawValue);
      value = Number.isNaN(parsed) ? null : parsed;
    } else if (type === 'bool') {
      value = rawValue === 'true';
    } else {
      value = rawValue;
    }
    putShipping({ [field]: value });
  };

  const formatCurrency = (value: number): string =>
    `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

  const formatReceiverAddress = (receiver?: ISalesCrmReceiverOption): string | undefined => {
    if (!receiver) return undefined;
    const houseAndApartment = [receiver.house_number, receiver.apartment_number]
      .filter((p) => p && p.trim() !== '')
      .join('/');
    const streetLine = [receiver.street, houseAndApartment].filter(Boolean).join(' ');
    const cityLine = [receiver.postal_code, receiver.city].filter(Boolean).join(' ');
    const parts: string[] = [];
    if (streetLine) parts.push(`ul. ${streetLine}`);
    if (cityLine) parts.push(cityLine);
    return parts.length > 0 ? parts.join(', ') : undefined;
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderClientOfferShippingDetailsContainer'
      )}>
      <div className={styles.body}>
        <div className={styles.main}>
          <Tabs
            value={activeTab}
            onChange={(_e, val) => setActiveTab(val)}
            className={styles.tabs}
            TabIndicatorProps={{ sx: { backgroundColor: '#6b54ff' } }}>
            <Tab label={t('WYSYŁKA')} className={styles.tab} />
            <Tab label={t('KOMENTARZE')} className={styles.tab} />
            <Tab label={t('UWAGI KLIENTA')} className={styles.tab} />
          </Tabs>

          {activeTab === 0 && (
            <div className={styles.tabPanel}>
              <fieldset className={styles.formFieldset} disabled={isSaving}>
                <div className={styles.formGrid}>
                  <TextField
                    label={t('Sposób dostawy')}
                    select
                    size="small"
                    value={shipping?.deliveryMethodId ?? ''}
                    onChange={(e) => handleFieldChange('deliveryMethodId', e.target.value, 'int')}
                    SelectProps={{ native: true, displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth>
                    <option value="">{t('Wybierz sposób dostawy')}</option>
                    {deliveryMethodsData?.items?.map((d) => (
                      <option key={d.id} value={d.delivery_method_id}>
                        {d.delivery_method ?? `#${d.delivery_method_id}`}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    label={t('Termin dostawy')}
                    type="date"
                    size="small"
                    value={shipping?.deliveryDate ?? ''}
                    onChange={(e) => handleFieldChange('deliveryDate', e.target.value, 'string')}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label={t('Koszt dostawy')}
                    type="number"
                    size="small"
                    placeholder="0"
                    defaultValue={shipping?.deliveryCost ?? ''}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      const current = shipping?.deliveryCost ?? null;
                      const parsed = v === '' ? null : parseFloat(v);
                      if (parsed !== current) {
                        handleFieldChange('deliveryCost', v, 'float');
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ endAdornment: <span className={styles.adornment}>zł</span> }}
                    fullWidth
                  />
                  <TextField
                    label={t('Adres dostawy')}
                    select
                    size="small"
                    value={shipping?.receiverId ?? ''}
                    onChange={(e) => handleFieldChange('receiverId', e.target.value, 'int')}
                    SelectProps={{ native: true, displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    FormHelperTextProps={{ className: styles.addressHelper }}
                    helperText={formatReceiverAddress(
                      receiversData?.items?.find((r) => r.id === shipping?.receiverId)
                    )}>
                    <option value="">{t('Wybierz adres')}</option>
                    {receiversData?.items?.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name ?? r.name_address ?? `#${r.id}`}
                      </option>
                    ))}
                  </TextField>
                </div>
              </fieldset>
            </div>
          )}

          {activeTab === 1 && (
            <div className={styles.tabPanel}>
              <OfferCommentsCard clientId={clientId} offerId={offerId} versionId={versionId} />
            </div>
          )}
          {activeTab === 2 && (
            <div className={styles.tabPanel}>
              <OfferCommentsCard
                clientId={clientId}
                offerId={offerId}
                versionId={versionId}
                clientOnly
                readOnly
              />
            </div>
          )}
        </div>

        <aside className={styles.summary}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('Produkt netto')}:</span>
            <span className={styles.summaryValueBold}>{formatCurrency(productNet)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('Wysyłka netto')}:</span>
            <span className={styles.summaryValueBold}>{formatCurrency(shippingNet)}</span>
          </div>

          {allServices.map((service) => (
            <div key={service.id} className={styles.summaryRow}>
              <span className={styles.summaryLabel}>
                {service.name}
                {service.priceType === 'UNIT' && ` (${t('jednostkowa')})`}:
              </span>
              <span className={styles.summaryValueBold}>
                {formatCurrency(service.valueNet ?? service.priceNet ?? 0)}
              </span>
            </div>
          ))}

          <div className={styles.summarySeparator} />

          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('Razem netto')}:</span>
            <span className={styles.summaryValueBold}>{formatCurrency(totalNet)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('VAT')}:</span>
            <span className={styles.summaryValueBold}>{formatCurrency(vat)}</span>
          </div>

          <div className={styles.summarySeparator} />

          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('Razem brutto')}:</span>
            <span className={styles.summaryGrandTotal}>{formatCurrency(totalGross)}</span>
          </div>

          <div className={styles.summarySeparator} />

          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('Marża kwotowa')}:</span>
            <span className={styles.summaryValueBold}>
              {marginNet !== null ? formatCurrency(marginNet) : '-'}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('Marża procentowa')}:</span>
            <span className={styles.summaryValueBold}>
              {marginPct !== null
                ? `${marginPct.toLocaleString('pl-PL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} %`
                : '-'}
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TraderClientOfferShippingDetailsContainer;
